import { Controller } from "@hotwired/stimulus"
import { patch, post } from "@rails/request.js"

export default class extends Controller {
  static targets = ["content", "editForm", "input"]
  static values = {
    cardId: String
  }

  connect() {
    this.editing = false
    this.saving = false
    this.boundBeforeUnload = this.beforeUnload.bind(this)
  }

  disconnect() {
    if (this.editing && !this.saving) {
      // Only send unlock beacon if the user didn't save — the server
      // already releases the lock on successful save. Without this guard,
      // the broadcast-triggered DOM replacement can race: disconnect fires
      // before the HTTP response arrives, sending a stale unlock that
      // cancels a freshly acquired lock on the next edit.
      this.sendUnlockBeacon()
    }
    this.editing = false
    this.saving = false
    delete this.element.dataset.selfEditing
    window.removeEventListener("beforeunload", this.boundBeforeUnload)
  }

  async edit(event) {
    event?.preventDefault()

    // Check if another user has locked this card
    const trigger = event?.currentTarget
    if (trigger?.dataset.editLocked === "true") {
      this.showToast("Cannot edit: another user is currently editing this card.")
      return
    }

    // Mark as self-editing BEFORE lock request to prevent race with broadcast
    this.element.dataset.selfEditing = "true"

    // Attempt to acquire the editing lock
    try {
      const response = await post(`/workspace/canvas_cards/${this.cardIdValue}/lock`, {
        responseKind: "json"
      })

      if (response.statusCode === 409) {
        delete this.element.dataset.selfEditing
        const data = await response.json
        this.showToast(data.error || "Cannot edit: another user is currently editing this card.")
        return
      }
    } catch (error) {
      // Network error — allow editing anyway (graceful degradation)
      console.warn("Could not acquire editing lock:", error)
    }

    this.editing = true
    window.addEventListener("beforeunload", this.boundBeforeUnload)

    this.contentTarget.classList.add("hidden")
    this.editFormTarget.classList.remove("hidden")
    // Use raw markdown content from data attribute, not rendered HTML
    this.inputTarget.value = this.contentTarget.dataset.rawContent || this.contentTarget.textContent.trim()
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  async cancel(event) {
    event?.preventDefault()
    this.editFormTarget.classList.add("hidden")
    this.contentTarget.classList.remove("hidden")

    if (this.editing) {
      this.editing = false
      delete this.element.dataset.selfEditing
      window.removeEventListener("beforeunload", this.boundBeforeUnload)
      await this.unlockCard()
    }
  }

  async save(event) {
    // Handle Enter key (without Shift) and blur
    if (event.type === "keydown") {
      if (event.key === "Escape") {
        this.cancel(event)
        return
      }
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
      } else {
        return
      }
    }

    const content = this.inputTarget.value.trim()

    if (!content) {
      this.cancel()
      return
    }

    try {
      // Mark saving before the request — the server releases the lock on
      // success and broadcasts a card replacement. If that broadcast arrives
      // via WebSocket before the HTTP response, disconnect() must know not
      // to send a stale unlock beacon.
      this.saving = true

      const response = await patch(`/workspace/canvas_cards/${this.cardIdValue}`, {
        body: JSON.stringify({ canvas_card: { content } }),
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        // Update raw content data attribute
        this.contentTarget.dataset.rawContent = content
        // Use rendered HTML from server if available, otherwise fall back to raw content
        if (data.rendered_content) {
          this.contentTarget.innerHTML = data.rendered_content
        } else {
          this.contentTarget.textContent = content
        }
        this.editFormTarget.classList.add("hidden")
        this.contentTarget.classList.remove("hidden")

        this.editing = false
        this.saving = false
        delete this.element.dataset.selfEditing
        window.removeEventListener("beforeunload", this.boundBeforeUnload)
        // Lock is released server-side in the update action

        // Show warning toast if there are linked hypotheses
        if (data.warning) {
          this.showToast(data.warning)
        }
      } else {
        this.saving = false
        const data = await response.json
        console.error("Failed to update card:", data.error)
      }
    } catch (error) {
      this.saving = false
      console.error("Error updating card:", error)
    }
  }

  async unlockCard() {
    try {
      await post(`/workspace/canvas_cards/${this.cardIdValue}/unlock`, {
        responseKind: "json"
      })
    } catch (error) {
      console.warn("Could not release editing lock:", error)
    }
  }

  sendUnlockBeacon() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const url = `/workspace/canvas_cards/${this.cardIdValue}/unlock`
    const data = new Blob([JSON.stringify({ authenticity_token: csrfToken })], { type: "application/json" })
    navigator.sendBeacon(url, data)
  }

  beforeUnload() {
    if (this.editing && !this.saving) {
      this.sendUnlockBeacon()
    }
  }

  showToast(message) {
    const toastContainer = document.getElementById("toast-container")
    if (!toastContainer) return

    const toast = document.createElement("div")
    toast.setAttribute("data-controller", "toast")
    toast.setAttribute("data-toast-duration-value", "5000")
    toast.className = "fixed z-50 flex items-center w-full max-w-xs p-4 rounded-base shadow-xs border top-4 end-4 text-body bg-neutral-primary-soft border-default"
    toast.setAttribute("role", "alert")
    toast.innerHTML = `
      <div class="flex justify-between w-full items-start">
        <div class="text-sm font-normal flex-1">${message}</div>
        <button type="button" data-action="click->toast#dismiss"
          class="shrink-0 ms-auto -mx-1.5 -my-1.5 rounded-md focus:ring-2 focus:ring-neutral-tertiary p-1.5 inline-flex items-center justify-center h-8 w-8 text-body hover:text-heading"
          aria-label="Close">
          <span class="sr-only">Close</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
        </button>
      </div>
    `
    toastContainer.prepend(toast)
  }
}
